/**
 * Local storage service for LiftForge
 * Handles persistence of application state to localStorage
 * Ported from index.html lines 982-1006 and legacy migration shim (lines 101-107)
 */

import { AppState } from '@/lib/types';
import { makeInitialState } from '@/lib/reducer';

const STORAGE_KEY = 'liftforge_state';
const LEGACY_STORAGE_KEY = 'workit_state';

/**
 * Save application state to localStorage
 * Excludes activeView and trackingSession (session-only), but saves _trackingSession if present
 */
export function saveLocal(state: AppState): void {
  try {
    const { trackingSession, gapiToken, ...data } = state;
    // Save trackingSession as _trackingSession to preserve mid-workout progress
    const toSave: any = { ...data };
    if (trackingSession) {
      toSave._trackingSession = trackingSession;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch (e) {
    console.warn('saveLocal:', e);
  }
}

/**
 * Load application state from localStorage
 * Returns null if missing or corrupt JSON
 * Restores trackingSession from _trackingSession and normalizes weeklySchedule
 */
export function loadLocal(): AppState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const data = JSON.parse(raw);
    const session = data._trackingSession || null;
    delete data._trackingSession;

    // Normalize weeklySchedule day names (handles legacy workit_state format)
    if (data.weeklySchedule) {
      data.weeklySchedule = normalizeWeeklySchedule(data.weeklySchedule);
    }

    // Apply schema migration
    const migrated = migrateSchema(data);

    return { ...migrated, trackingSession: session };
  } catch (e) {
    console.warn('loadLocal:', e);
    return null;
  }
}

/**
 * Clear application state from localStorage
 */
export function clearLocal(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.warn('clearLocal:', e);
  }
}

/**
 * Run legacy migration from 'workit_state' to 'liftforge_state'
 * Also migrates session storage and other keys
 */
export function runLegacyMigration(): void {
  const lsMigrate = [
    ['workit_state', 'liftforge_state'],
    ['workit_session', 'liftforge_session'],
    ['workit_last_synced', 'liftforge_last_synced'],
    ['workit_ai_config', 'liftforge_ai_config'],
    ['workit_ai_cache', 'liftforge_ai_cache'],
    ['workit_ai_usage', 'liftforge_ai_usage'],
  ];

  const ssMigrate = [
    ['workit_token', 'liftforge_token'],
    ['workit_coach_enabled', 'liftforge_coach_enabled'],
  ];

  // Migrate localStorage keys
  lsMigrate.forEach(([oldKey, newKey]) => {
    const value = localStorage.getItem(oldKey);
    if (value !== null && localStorage.getItem(newKey) === null) {
      localStorage.setItem(newKey, value);
      localStorage.removeItem(oldKey);
    }
  });

  // Migrate sessionStorage keys
  ssMigrate.forEach(([oldKey, newKey]) => {
    const value = sessionStorage.getItem(oldKey);
    if (value !== null && sessionStorage.getItem(newKey) === null) {
      sessionStorage.setItem(newKey, value);
      sessionStorage.removeItem(oldKey);
    }
  });
}

/**
 * Normalize weeklySchedule day names for backward compatibility
 * Handles legacy format where days might have different names
 */
function normalizeWeeklySchedule(schedule: any[]): any[] {
  if (!Array.isArray(schedule)) return schedule;

  const DAYS = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];

  return schedule.map((slot, index) => ({
    day: slot.day || DAYS[index] || `Day ${index + 1}`,
    workoutId: slot.workoutId || null,
  }));
}

/**
 * Migrate schema from older versions to current version
 * Currently targets schemaVersion 2
 * Preserves unknown keys for forward compatibility
 */
export function migrateSchema(data: any): AppState {
  // If no schemaVersion or version < 2, perform migration
  if (!data.schemaVersion || data.schemaVersion < 2) {
    // Get initial state to fill in missing fields
    const initial = makeInitialState();

    // Merge: preserve loaded data, fill in missing fields from initial state
    const migrated = { ...initial, ...data };

    // Ensure schemaVersion is set
    migrated.schemaVersion = 2;

    return migrated as AppState;
  }

  // Already at current version, just ensure type safety
  return data as AppState;
}
